import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { getOrder } from "./api";
import AppDownloadNudge from "./components/AppDownloadNudge";
import { analytics, EVENTS } from "../../analytics";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 20000;

export default function OrderConfirmation() {
  const { shopId, orderId } = useParams();
  const navigate = useNavigate();
  const { width, height } = useWindowSize();

  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const startedAt = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await getOrder(orderId);
        if (cancelled) return;
        setOrder(res.data);
        if (res.data.isPaid || res.data.totalAmount === 0) {
          setStatus("paid");
          analytics.track(EVENTS.ORDER_CONFIRMATION_VIEW, { order_id: orderId });
          return;
        }
        if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
          setStatus("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return (
    <div className="min-h-screen flex flex-col items-center px-6 pt-16 pb-8 text-center">
      {status === "paid" && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={400}
          gravity={0.15}
        />
      )}

      <div className="w-20 h-20 rounded-full otaGradientButton flex items-center justify-center mb-5">
        {status === "checking" ? (
          <span className="text-white text-2xl animate-pulse">…</span>
        ) : (
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 13l4 4L19 7"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      <h1 className="text-xl font-bold otaDark">
        {status === "checking" && "Confirming your order…"}
        {status === "paid" && "Order Confirmed!"}
        {status === "pending" && "Payment Processing"}
        {status === "error" && "We're checking on your order"}
      </h1>

      <p className="otaLight text-sm mt-2 max-w-[280px]">
        {status === "pending"
          ? "This is taking a little longer than usual — you'll see it in your order history once confirmed."
          : "Thanks for shopping with us! Show this screen at the stall if needed."}
      </p>

      {order && (
        <div className="w-full rounded-xl border border-gray-100 cardShadow p-4 mt-6 text-left">
          <div className="flex justify-between text-sm otaLight">
            <span>Order</span>
            <span className="otaDark font-medium">#{order.orderId?.slice(-8)}</span>
          </div>
          <div className="flex justify-between text-sm otaLight mt-1">
            <span>Total paid</span>
            <span className="otaDark font-semibold">
              ₹{order.totalAmount?.toFixed(0)}
            </span>
          </div>
        </div>
      )}

      <div className="w-full mt-8">
        <AppDownloadNudge />
      </div>

      <button
        onClick={() => navigate(`/shop/${shopId}`)}
        className="mt-6 text-sm font-semibold otaFont"
      >
        Continue shopping
      </button>
    </div>
  );
}
